import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { bookingService } from '../../services/bookingService';

// Direct call — process-refund uses service role internally
async function callProcessRefund(body) {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  const res = await fetch(`${supabaseUrl}/functions/v1/process-refund`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'apikey': anonKey, 'Authorization': `Bearer ${anonKey}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `خطأ ${res.status}`);
  return data;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n) => Number(n || 0).toLocaleString('ar-SA-u-nu-latn', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const formatDateShort = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { year: 'numeric', month: 'short', day: 'numeric' });
};

// ─── Main Component ────────────────────────────────────────────────────────────

export default function AdminRefunds() {
  const navigate = useNavigate();

  // source filter: 'all' | 'photographer' | 'customer'
  const [source, setSource] = useState('all');
  // status tab (only for photographer withdrawal)
  const [tab, setTab] = useState('pending');

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [notice, setNotice] = useState(null);

  // Modal states
  const [refundModal, setRefundModal] = useState(null);    // { bookingId, amount, customerName }
  const [approveModal, setApproveModal] = useState(null);  // { bookingId, refundAmount, feePct, refundPct, customerName }
  const [adminNote, setAdminNote] = useState('');

  // Stats
  const [stats, setStats] = useState({
    photographerPending: 0, photographerPendingAmount: 0,
    customerPending: 0,     customerPendingAmount: 0,
    completedMonth: 0,      completedMonthAmount: 0,
  });

  const showNotice = (type, msg) => {
    setNotice({ type, msg });
    setTimeout(() => setNotice(null), 5000);
  };

  // ─── Stats ────────────────────────────────────────────────────────────────

  const loadStats = useCallback(async () => {
    try {
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

      const [photographerRes, customerRes, completedRes] = await Promise.all([
        // Photographer withdrawals pending refund
        supabase.from('bookings')
          .select('final_price_sar, price')
          .eq('status', 'cancelled_by_photographer')
          .eq('payment_status', 'refund_pending'),

        // Customer cancellation requests pending
        supabase.from('bookings')
          .select('final_price_sar, price, cancellation_fee_pct')
          .eq('status', 'cancellation_requested'),

        // Completed refunds this month (both types)
        supabase.from('bookings')
          .select('final_price_sar, price, cancellation_fee_pct, status, refund_processed_at, updated_at')
          .eq('payment_status', 'refunded')
          .or(`status.eq.cancelled_by_photographer,status.eq.cancelled_by_customer`)
          .gte('updated_at', monthStart),
      ]);

      const photographerRows = photographerRes.data || [];
      const customerRows     = customerRes.data || [];
      const completedRows    = completedRes.data || [];

      const calcRefund = (r) => {
        const total = r.final_price_sar || r.price || 0;
        const fee   = r.cancellation_fee_pct || 0;
        return Math.round(total * (1 - fee / 100));
      };

      setStats({
        photographerPending:       photographerRows.length,
        photographerPendingAmount: photographerRows.reduce((s, r) => s + (r.final_price_sar || r.price || 0), 0),
        customerPending:           customerRows.length,
        customerPendingAmount:     customerRows.reduce((s, r) => s + calcRefund(r), 0),
        completedMonth:            completedRows.length,
        completedMonthAmount:      completedRows.reduce((s, r) => s + calcRefund(r), 0),
      });
    } catch (e) {
      console.error('loadStats error:', e);
    }
  }, []);

  // ─── Data loader ──────────────────────────────────────────────────────────

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const baseFields = 'id, status, booking_date, booking_time, price, final_price_sar, payment_status, payment_transaction_id, customer_id, assigned_photographer_id, cancellation_reason, cancellation_requested_at, cancellation_fee_pct, refund_processed_at, refund_reference, refund_admin_note, updated_at';

      const queries = [];

      // ── Type A: photographer withdrawal refunds ───────────────────────────
      if (source === 'all' || source === 'photographer') {
        let q = supabase.from('bookings').select(baseFields)
          .eq('status', 'cancelled_by_photographer')
          .order('cancellation_requested_at', { ascending: false });

        if (tab === 'pending')   q = q.eq('payment_status', 'refund_pending');
        else if (tab === 'done') q = q.eq('payment_status', 'refunded');
        else                     q = q.in('payment_status', ['refund_pending', 'refunded']);

        queries.push(q.then(r => (r.data || []).map(b => ({ ...b, _type: 'photographer' }))));
      }

      // ── Type B: customer cancellation requests (pending + completed) ────────
      if (source === 'all' || source === 'customer') {
        // Pending: waiting for admin decision
        const qPending = supabase.from('bookings').select(baseFields)
          .eq('status', 'cancellation_requested')
          .order('cancellation_requested_at', { ascending: false });

        // Completed: admin already approved → status became cancelled_by_customer
        const qDone = supabase.from('bookings').select(baseFields)
          .eq('status', 'cancelled_by_customer')
          .eq('payment_status', 'refunded')
          .order('cancellation_requested_at', { ascending: false });

        if (tab === 'pending') {
          queries.push(qPending.then(r => (r.data || []).map(b => ({ ...b, _type: 'customer' }))));
        } else if (tab === 'done') {
          queries.push(qDone.then(r => (r.data || []).map(b => ({ ...b, _type: 'customer' }))));
        } else {
          // 'all' — both pending and completed
          queries.push(
            Promise.all([qPending, qDone]).then(([rp, rd]) => [
              ...(rp.data || []).map(b => ({ ...b, _type: 'customer' })),
              ...(rd.data || []).map(b => ({ ...b, _type: 'customer' })),
            ])
          );
        }
      }

      const results = await Promise.all(queries);
      const allRows = results.flat();

      if (!allRows.length) { setItems([]); return; }

      // Enrich with names
      const customerIds     = [...new Set(allRows.map(b => b.customer_id).filter(Boolean))];
      const photographerIds = [...new Set(allRows.map(b => b.assigned_photographer_id).filter(Boolean))];

      const [custRes, ppRes, puRes] = await Promise.all([
        customerIds.length
          ? supabase.from('user_profiles').select('id, full_name, phone').in('id', customerIds)
          : Promise.resolve({ data: [] }),
        photographerIds.length
          ? supabase.from('photographer_profiles').select('user_id, business_name').in('user_id', photographerIds)
          : Promise.resolve({ data: [] }),
        photographerIds.length
          ? supabase.from('user_profiles').select('id, full_name').in('id', photographerIds)
          : Promise.resolve({ data: [] }),
      ]);

      const customerMap     = Object.fromEntries((custRes.data || []).map(u => [u.id, u]));
      const ppMap           = Object.fromEntries((ppRes.data   || []).map(p => [p.user_id, p.business_name]));
      const puMap           = Object.fromEntries((puRes.data   || []).map(u => [u.id, u.full_name]));

      setItems(allRows.map(b => {
        const total      = b.final_price_sar || b.price || 0;
        const feePct     = b.cancellation_fee_pct || 0;
        const refundAmt  = b._type === 'customer' ? Math.round(total * (1 - feePct / 100)) : total;
        return {
          ...b,
          _total:      total,
          _refundAmt:  refundAmt,
          _feePct:     feePct,
          customer:    customerMap[b.customer_id] || {},
          photographerName: ppMap[b.assigned_photographer_id] || puMap[b.assigned_photographer_id] || '—',
        };
      }).sort((a, b) => new Date(b.cancellation_requested_at) - new Date(a.cancellation_requested_at)));
    } catch (e) {
      console.error('loadItems error:', e);
      showNotice('error', 'فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  }, [source, tab]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadItems(); }, [loadItems]);

  // ─── Actions ──────────────────────────────────────────────────────────────

  // Photographer withdrawal → process-refund edge function
  const handleRefund = async (bookingId) => {
    const note = adminNote.trim();
    setRefundModal(null);
    setAdminNote('');
    setProcessingId(bookingId);
    try {
      const result = await callProcessRefund({ bookingId, adminNote: note || undefined });
      if (result?.success) {
        showNotice('success', `✅ تم الاسترداد بنجاح (${result.gateway === 'manual' ? 'يدوي' : result.gateway})`);
        loadItems(); loadStats();
      } else throw new Error(result?.error || 'فشل الاسترداد');
    } catch (err) {
      showNotice('error', `❌ ${err.message}`);
    } finally {
      setProcessingId(null);
    }
  };

  // Customer cancellation → approve + auto-refund via Moyasar
  const handleApprove = async (bookingId) => {
    const refundAmt = approveModal?.refundAmount;
    setApproveModal(null);
    setAdminNote('');
    setProcessingId(bookingId);
    try {
      // Process Moyasar refund first (before resolveCancellationRequest sets payment_status='refunded')
      const result = await callProcessRefund({
        bookingId,
        refundAmountSar: refundAmt != null ? refundAmt : undefined,
      });
      // Then update booking status to cancelled_by_customer
      await bookingService.resolveCancellationRequest(bookingId, true);
      showNotice('success', `✅ تم قبول الإلغاء وإرجاع المبلغ تلقائياً (${result.gateway === 'manual' ? 'يدوي' : result.gateway})`);
      loadItems(); loadStats();
    } catch (err) {
      showNotice('error', `❌ ${err.message}`);
    } finally {
      setProcessingId(null);
    }
  };

  // Customer cancellation → reject
  const handleReject = async (bookingId) => {
    if (!window.confirm('هل تريد رفض طلب الإلغاء؟ سيعود الحجز إلى حالة مؤكد.')) return;
    setProcessingId(bookingId);
    try {
      await bookingService.resolveCancellationRequest(bookingId, false);
      showNotice('success', 'تم رفض طلب الإلغاء — الحجز عاد إلى مؤكد');
      loadItems(); loadStats();
    } catch (err) {
      showNotice('error', `❌ ${err.message || 'فشل رفض الطلب'}`);
    } finally {
      setProcessingId(null);
    }
  };

  // ─── Filter ───────────────────────────────────────────────────────────────

  const filtered = items.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      r.customer?.full_name?.toLowerCase().includes(s) ||
      r.customer?.phone?.includes(s) ||
      r.photographerName?.toLowerCase().includes(s) ||
      r.id?.toLowerCase().includes(s)
    );
  });

  const pendingTotal = stats.photographerPending + stats.customerPending;

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8" dir="rtl">

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/admin')} className="p-2 rounded-xl hover:bg-slate-200 transition-colors">
          <Icon name="ArrowRight" size={20} className="text-slate-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">إدارة الاستردادات</h1>
          <p className="text-sm text-slate-500">استردادات انسحاب المصور وطلبات إلغاء العميل</p>
        </div>
        <button onClick={() => { loadItems(); loadStats(); }} className="mr-auto p-2 rounded-xl hover:bg-slate-200 transition-colors" title="تحديث">
          <Icon name="RefreshCw" size={18} className="text-slate-500" />
        </button>
      </div>

      {/* Notice */}
      {notice && (
        <div className={`mb-4 px-4 py-3 rounded-xl text-sm font-medium flex items-center gap-2 ${notice.type === 'success' ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-red-100 text-red-800 border border-red-300'}`}>
          <Icon name={notice.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={16} />
          {notice.msg}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <StatCard icon="Camera" color="orange" label="معلق — انسحاب مصور"
          value={stats.photographerPending}
          sub={`${fmt(stats.photographerPendingAmount)} ر.س`} />
        <StatCard icon="User" color="red" label="معلق — إلغاء عميل"
          value={stats.customerPending}
          sub={`${fmt(stats.customerPendingAmount)} ر.س مُسترَد`} />
        <StatCard icon="CheckCircle" color="green" label="تمّ الاسترداد هذا الشهر"
          value={stats.completedMonth}
          sub={`${fmt(stats.completedMonthAmount)} ر.س`} />
      </div>

      {/* Source filter + Tab + Search */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex flex-wrap items-center gap-3">

          {/* Source type */}
          <div className="flex gap-1 bg-slate-100 p-1 rounded-xl">
            {[
              { id: 'all',          label: 'الكل',             count: pendingTotal },
              { id: 'photographer', label: '📷 انسحاب مصور',   count: stats.photographerPending },
              { id: 'customer',     label: '🙋 إلغاء عميل',    count: stats.customerPending },
            ].map(s => (
              <button key={s.id} onClick={() => setSource(s.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${source === s.id ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>
                {s.label}
                {s.count > 0 && (
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${source === s.id ? 'bg-orange-500 text-white' : 'bg-slate-200 text-slate-600'}`}>{s.count}</span>
                )}
              </button>
            ))}
          </div>

          {/* Status tab — only relevant for photographer withdrawals */}
          {source !== 'customer' && (
            <div className="flex gap-1 bg-slate-100 p-1 rounded-xl">
              {[
                { id: 'pending', label: 'قيد الانتظار' },
                { id: 'done',    label: 'تمّ الاسترداد' },
                { id: 'all',     label: 'الكل' },
              ].map(t => (
                <button key={t.id} onClick={() => setTab(t.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${tab === t.id ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {/* Search */}
          <div className="relative mr-auto">
            <Icon name="Search" size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="بحث باسم العميل أو المصور..."
              className="pr-9 pl-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 w-64" />
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="p-12 text-center text-slate-400">
            <Icon name="Loader" size={32} className="mx-auto mb-2 animate-spin" />
            <p>جاري التحميل...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <Icon name="CheckCircle" size={48} className="mx-auto mb-3 text-green-400" />
            <p className="font-semibold text-slate-600">لا توجد طلبات معلقة</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map(r => (
              <RefundItem
                key={r.id}
                item={r}
                processingId={processingId}
                onRefund={() => { setRefundModal({ bookingId: r.id, amount: r._refundAmt, customerName: r.customer?.full_name }); setAdminNote(''); }}
                onApprove={() => { setApproveModal({ bookingId: r.id, refundAmount: r._refundAmt, feePct: r._feePct, refundPct: 100 - r._feePct, customerName: r.customer?.full_name }); setAdminNote(''); }}
                onReject={() => handleReject(r.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Refund Confirm Modal (photographer withdrawal) ── */}
      {refundModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" dir="rtl">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <Icon name="DollarSign" size={20} className="text-green-700" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">تأكيد الاسترداد</h3>
                <p className="text-xs text-slate-500">{refundModal.customerName || '—'}</p>
              </div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4 text-center">
              <p className="text-2xl font-black text-green-700">{fmt(refundModal.amount)} ر.س</p>
              <p className="text-xs text-green-600 mt-1">المبلغ الكامل — انسحاب المصور</p>
            </div>
            <div className="mb-4">
              <label className="text-sm font-medium text-slate-700 block mb-1.5">ملاحظة (اختياري)</label>
              <textarea rows={2} value={adminNote} onChange={e => setAdminNote(e.target.value)}
                placeholder="مثال: تم التحويل البنكي في..."
                className="w-full text-sm border border-slate-200 rounded-xl p-3 resize-none focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div className="flex gap-2">
              <button onClick={() => handleRefund(refundModal.bookingId)}
                className="flex-1 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-bold rounded-xl transition-colors">
                تأكيد الاسترداد
              </button>
              <button onClick={() => setRefundModal(null)}
                className="px-4 py-2.5 border border-slate-200 text-slate-600 text-sm rounded-xl hover:bg-slate-50 transition-colors">
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Approve Modal (customer cancellation) ── */}
      {approveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" dir="rtl">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <Icon name="CheckSquare" size={20} className="text-amber-700" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">قبول طلب الإلغاء</h3>
                <p className="text-xs text-slate-500">{approveModal.customerName || '—'}</p>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">رسوم الإلغاء</span>
                <span className="font-bold text-red-600">{approveModal.feePct}%</span>
              </div>
              <div className="flex justify-between text-sm border-t border-amber-200 pt-2">
                <span className="text-slate-500">المبلغ المُسترَد للعميل</span>
                <span className="font-black text-green-700">{fmt(approveModal.refundAmount)} ر.س ({approveModal.refundPct}%)</span>
              </div>
            </div>
            <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-4">
              ✅ سيتم إرجاع المبلغ تلقائياً للعميل عبر ميسر عند الضغط على قبول.
            </p>
            <div className="flex gap-2">
              <button onClick={() => handleApprove(approveModal.bookingId)}
                className="flex-1 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-bold rounded-xl transition-colors">
                قبول الإلغاء
              </button>
              <button onClick={() => setApproveModal(null)}
                className="px-4 py-2.5 border border-slate-200 text-slate-600 text-sm rounded-xl hover:bg-slate-50 transition-colors">
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Unified Row/Card ─────────────────────────────────────────────────────────

function RefundItem({ item: r, processingId, onRefund, onApprove, onReject }) {
  const isProcessing = processingId === r.id;
  const isPhotographer = r._type === 'photographer';
  const isCustomer     = r._type === 'customer';

  // Status
  const isDone    = r.payment_status === 'refunded' || r.status === 'cancelled_by_customer';
  const isPending = isPhotographer ? r.payment_status === 'refund_pending' : !isDone;

  const daysSince = r.cancellation_requested_at
    ? Math.floor((Date.now() - new Date(r.cancellation_requested_at).getTime()) / 86400000)
    : null;
  const urgent = isPending && daysSince !== null && daysSince >= 3;

  return (
    <div className="p-4 hover:bg-slate-50/60 transition-colors">
      <div className="flex items-start gap-3">
        {/* Type icon */}
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${isPhotographer ? 'bg-orange-100' : 'bg-blue-100'}`}>
          <Icon name={isPhotographer ? 'Camera' : 'User'} size={16} className={isPhotographer ? 'text-orange-600' : 'text-blue-600'} />
        </div>

        <div className="flex-1 min-w-0">
          {/* Top row */}
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <p className="font-bold text-slate-900 text-sm">{r.customer?.full_name || '—'}</p>
              {r.customer?.phone && <p className="text-xs text-slate-400" dir="ltr">{r.customer.phone}</p>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {/* Type badge */}
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isPhotographer ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                {isPhotographer ? 'انسحاب مصور' : 'إلغاء عميل'}
              </span>
              {/* Status badge */}
              {isDone ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                  <Icon name="CheckCircle" size={9} /> تمّ
                </span>
              ) : (
                <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${urgent ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`}>
                  <Icon name="Clock" size={9} /> معلق {daysSince !== null && daysSince > 0 ? `• ${daysSince}ي` : ''}
                  {urgent && ' ⚠️'}
                </span>
              )}
            </div>
          </div>

          {/* Details row */}
          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-slate-500">
            <span>📷 {r.photographerName}</span>
            <span>📅 {formatDateShort(r.booking_date)}</span>
            {isCustomer && r._feePct > 0 && (
              <span className="text-red-500">رسوم {r._feePct}%</span>
            )}
          </div>
          {r.cancellation_reason && (
            <p className="mt-1 text-xs text-slate-400 truncate">السبب: {r.cancellation_reason}</p>
          )}

          {/* Amount + Actions */}
          <div className="mt-2.5 flex items-center justify-between gap-2 flex-wrap">
            <div>
              <span className="text-sm font-black text-green-700">{fmt(r._refundAmt)} ر.س</span>
              {isCustomer && r._feePct > 0 && (
                <span className="text-xs text-slate-400 mr-1">({100 - r._feePct}% من {fmt(r._total)})</span>
              )}
              {isDone && (
                <p className="text-[10px] text-green-600 mt-0.5">
                  {isPhotographer
                    ? (r.refund_processed_at ? formatDate(r.refund_processed_at) : '—')
                    : `تم قبول الإلغاء · ${formatDate(r.updated_at)}`}
                </p>
              )}
            </div>

            {/* Actions */}
            {!isDone && (
              <div className="flex gap-2">
                {isPhotographer && (
                  <button onClick={onRefund} disabled={!!processingId}
                    className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-xs font-bold rounded-lg transition-colors">
                    {isProcessing ? <Icon name="Loader" size={12} className="animate-spin" /> : <Icon name="DollarSign" size={12} />}
                    {isProcessing ? 'جاري...' : 'استرداد'}
                  </button>
                )}
                {isCustomer && (
                  <>
                    <button onClick={onReject} disabled={!!processingId}
                      className="px-3 py-1.5 border border-slate-200 text-slate-600 text-xs font-semibold rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors">
                      {isProcessing ? '...' : 'رفض'}
                    </button>
                    <button onClick={onApprove} disabled={!!processingId}
                      className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-xs font-bold rounded-lg transition-colors">
                      {isProcessing ? <Icon name="Loader" size={12} className="animate-spin" /> : <Icon name="Check" size={12} />}
                      {isProcessing ? 'جاري...' : 'قبول الإلغاء'}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

const colorMap = {
  orange: { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'text-orange-600', val: 'text-orange-700' },
  red:    { bg: 'bg-red-50',    border: 'border-red-200',    icon: 'text-red-600',    val: 'text-red-700'    },
  green:  { bg: 'bg-green-50',  border: 'border-green-200',  icon: 'text-green-600',  val: 'text-green-700'  },
};

function StatCard({ icon, color, label, value, sub }) {
  const c = colorMap[color];
  return (
    <div className={`${c.bg} ${c.border} border rounded-2xl p-4`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon name={icon} size={16} className={c.icon} />
        <p className="text-xs text-slate-500 font-medium">{label}</p>
      </div>
      <p className={`text-xl font-black ${c.val}`}>{value}</p>
      <p className="text-xs text-slate-400 mt-0.5">{sub}</p>
    </div>
  );
}
